import React, { useEffect, useState } from 'react';
import { mockOverviewData, fetchOverviewData } from '../data/mockData';
import { isAuthenticated } from '../utils/auth';
import EthAmount from '../components/EthAmount';
import './Overview.css';

const Overview: React.FC = () => {
  const [profitAndLoss, setProfitAndLoss] = useState(mockOverviewData.profitAndLoss);
  const [cost, setCost] = useState(mockOverviewData.cost);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const data = await fetchOverviewData();
        if (!mounted) return;
        setProfitAndLoss(data.profitAndLoss);
        setCost(data.cost);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load overview');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (!isAuthenticated()) {
    window.location.href = '/login';
    return null;
  }

  return (
    <div className="overview-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="title-icon overview"></span>
          Overview
        </h1>
      </div>

      <div className="content">
        {loading && <div className="loading">Loading overview…</div>}
        {error && <div className="error">{error}</div>}
        {/* Profit and Loss Card */}
        <div className="card profit-loss-card">
          <div className="card-title">
            <span className="title-icon overview"></span>
            Profit and Loss
          </div>
          
          <div className="metrics-grid">
            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  Revenue
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={profitAndLoss.revenue} className="card" />
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">
                  Profit
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value green-text">
                  <EthAmount amount={profitAndLoss.profit} className="card" />
                </div>
              </div>
            </div>

            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  Total Cost
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={profitAndLoss.totalCost} className="card" />
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">
                  Max Profit
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value green-text">
                  <EthAmount amount={profitAndLoss.maxProfit} className="card" />
                </div>
              </div>
            </div>

            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  Potential Loss
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={profitAndLoss.potentialLoss} className="card" />
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-label">
                  Min Profit
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value green-text">
                  <EthAmount amount={profitAndLoss.minProfit} className="card" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Card */}
        <div className="card cost-card">
          <div className="card-title">
            <span className="title-icon">💰</span>
            Cost
          </div>
          
          <div className="metrics-grid">
            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  ETH Locked In Pool
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={cost.ethLockedInPool} className="card" />
                </div>
              </div>
            </div>

            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  Gas
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={cost.gas} className="card" />
                </div>
              </div>
            </div>

            <div className="metric-column">
              <div className="metric-item">
                <div className="metric-label">
                  Other Cost
                  <span className="info-icon">ℹ️</span>
                </div>
                <div className="metric-value">
                  <EthAmount amount={cost.otherCost} className="card" />
                  <span className="chain-icon">🔗</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
